import React, { useState, useEffect, useCallback } from 'react';
import {
	HStack,
	Box,
	Image,
	Text,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	Flex,
	Button,
	useDisclosure,
	useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { LOGO_SRC as Headerlogo } from '../theme/brand';
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../index.css";
import { fetchWrapper } from '../helpers';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import { FiAlertCircle } from 'react-icons/fi';
import { BsShield } from 'react-icons/bs';
import { TwoFactorAuthModal } from './TwoFaModal';
import usePagePermissions, { clearPermissionCache } from '../hooks/usePagePermissions';
import { NavMegaMenu, NavTopLink } from './navigation/MegaMenu';

// Backend slugs for nodes that are meant to be dropdown GROUPS (parents),
// never direct links. If the backend returns one of these with zero
// children — or every child gets filtered out elsewhere by the
// permission-driven nav list — it must be hidden entirely rather than fall
// through to the leaf-link render path (PERM-125/171: an empty dropdown
// trigger, or worse a dead link to a route that doesn't exist, is worse than
// just not showing the item).
const PARENT_NAV_SLUGS = [
	'contact-aud',
	'campaigns-ourneys',
	'content',
	'settings',
	'message-logs-main',
	'reports',
	'analytics',
	'documentation',
	'user-management',
	'support',
];

const isEmptyParentNode = (node) =>
	PARENT_NAV_SLUGS.includes(node.slug) && (!node.children || node.children.length === 0);

function Header({ header }) {

	

	const [email, setEmail] = useState('');
	const [permissions, setPermissions] = useState([]);
	const { isOpen, onOpen, onClose } = useDisclosure();
	// Separate from the nav `permissions` above (that's the JWT-decoded page
	// tree) — this is the granular ticket-list/ticket-create action check for
	// whether the floating Contact Support button should show at all.
	const { can: canTicket } = usePagePermissions('support/tickets');
	const showSupportButton = canTicket('ticket-list') || canTicket('ticket-create');

	const location = useLocation();
	const navigate = useNavigate();

	/**
	 * Is `slug` the nav entry for the current route?
	 *
	 * Matches on PATH SEGMENTS, never as a substring. The parent-dropdown check used
	 * `location.pathname.includes(child.slug)`, so a slug that happens to be a prefix of
	 * another route lit up two menus at once: on /documentation/webhook-doc, the group
	 * holding `webhook` matched because "/documentation/webhook-doc".includes("webhook")
	 * is true. Requiring either an exact match or a following "/" fixes that while
	 * keeping detail routes (/insights/123 → `insights`) highlighted under their parent.
	 *
	 * Note "/webhook-doc" does NOT match `webhook`: a hyphen is not a segment boundary,
	 * which is the whole point.
	 */
	const isNavActive = useCallback((slug) => {
		if (!slug) return false;
		const target = `/${String(slug).replace(/^\/+/, '')}`;
		return location.pathname === target || location.pathname.startsWith(`${target}/`);
	}, [location.pathname]);

	/**
	 * Does `node` (or any of its descendants, at any depth) match the current
	 * route? Used to light up an ancestor dropdown/group when the active page
	 * is nested two or more levels down (e.g. Settings → User Management → User).
	 */
	const hasActiveDescendant = useCallback((node) => {
		if (isNavActive(node.slug)) return true;
		return (node.children || []).some(hasActiveDescendant);
	}, [isNavActive]);

	/* ---------------- 2FA BANNER (ONE TIME) ---------------- */
	const [showBanner, setShowBanner] = useState(() => {
		const mfaEnabled = localStorage.getItem('mfa_enabled') === 'true';
		const mfaVerified = localStorage.getItem('mfa_verified') === 'true';
		const twofaRequired = localStorage.getItem('twofa_required') === 'false';

		return !twofaRequired && !(mfaEnabled && mfaVerified);
	});

	const handleSuccess = () => {
		setShowBanner(false);
	};

	/* ---------------- TOKEN DECODE (SINGLE UPDATE) ---------------- */
	useEffect(() => {
		const token = localStorage.getItem('customer-portal');
		if (!token) return;

		try {
			const decodedToken = jwtDecode(token);
			const Userdata = decodedToken?.userdata;

			if (!Userdata) return;

			// ✅ batch state update
			setEmail(Userdata.username || '');
			setPermissions(Userdata.permission || []);

			localStorage.setItem("customer", JSON.stringify(Userdata));
		} catch (error) {
			console.error("Failed to decode token:", error);
			toast.error("Invalid session, please log in again.");
		}
	}, []); // ❌ removed navigate dependency

	/* ---------------- LOGOUT ---------------- */
	const Logout = useCallback(async () => {
		const url = `${process.env.REACT_APP_API_URL}/user/logout`;
		const res = await fetchWrapper.post(url, {});

		if (res?.success) {
			localStorage.removeItem('user');
			localStorage.removeItem('customer-portal');
			clearPermissionCache();
			navigate("/");
		} else {
			toast.error("Logout failed");
		}
	}, [navigate]);

	/* ---------------- MENU RENDER ---------------- */
	// A top-level page is either a plain link or a group, which opens a mega-menu
	// panel of its children (see components/navigation/MegaMenu.jsx).
	const renderMenuItem = useCallback((page) => {
		if (isEmptyParentNode(page)) return null;
		if (!page.children || page.children.length === 0) {
			return (
				<Box me={1} key={page.id} flexShrink={0}>
					<NavTopLink node={page} isActive={isNavActive(page.slug)} />
				</Box>
			);
		}
		return (
			<Box me={1} key={page.id} flexShrink={0}>
				<NavMegaMenu
					label={page.name}
					header={page}
					nodes={page.children}
					isActive={page.children.some(hasActiveDescendant)}
					isNavActive={isNavActive}
					isHidden={isEmptyParentNode}
				/>
			</Box>
		);
	}, [isNavActive, hasActiveDescendant]);

	const NAV_VISIBLE = useBreakpointValue({ base: 2, sm: 4, md: 6, lg: 9, xl: 12, '2xl': 999 }) ?? 12;

	// Support moved out of the top nav entirely — it's reached via the floating
	// support button below instead, so drop it before the nav even considers
	// visible-vs-"More" slots.
	// 'finance' has no route in this portal (it's an admin-portal-only page per
	// the backend's pages table) — filtered defensively here too, though the
	// real fix is removing/re-parenting that pages-table row on the backend.
	const navItems = permissions
		.filter((page) => page.slug !== 'support/tickets' && page.slug !== 'finance')
		// PERM-125/171: drop parent-type nodes that resolved to zero children
		// (via the backend or via permission filtering above) — they must not
		// take up a visible/overflow nav slot as an empty dropdown trigger.
		.filter((page) => !isEmptyParentNode(page));

	return (
		<>
			{showBanner && (
				<Box
					bgGradient="linear(to-r, orange.500, yellow.500)"
					borderBottom="1px"
					borderColor="orange.600"
				>
					<Box mx="auto" px={16} py={3}>
						<Flex alignItems="center" justifyContent="space-between">
							<Flex alignItems="center" gap={3}>
								<Box bg="whiteAlpha.200" borderRadius="full" p={1.5}>
									<FiAlertCircle size={20} color="white" />
								</Box>
								<Text color="white" fontSize="sm">
									<strong>Security Alert:</strong> Two-Factor Authentication is required for your account. Please set up 2FA using Google Authenticator to secure your account.
								</Text>
							</Flex>
							<Button
								size="sm"
								onClick={onOpen}
								bg="white"
								color="#f54a00"
								_hover={{ bg: '#fff7ed !important', color: '#f97316 !important' }}
								flexShrink={0}
								ml={4}
								leftIcon={<BsShield size={16} />}
							>
								Enable 2FA
							</Button>
						</Flex>
					</Box>
				</Box>
			)}

			{/* Top Header */}
			<HStack px="70px" py="10px" justifyContent="space-between" className="top_header_section">
				<Box>
					<Link to="/dashboard">
						{/* Logo comes from REACT_APP_LOGO_URL, so its aspect ratio is unknown at
						    build time. Bound BOTH axes and let objectFit scale it down to fit:
						    a wide lockup fills the width, a square mark stays square. Never set
						    only one axis here — that stretches or shrinks off-brand logos. */}
						<Image src={Headerlogo} alt='Logo' w="auto" h="auto" maxH="36px" maxW="150px" objectFit="contain" />
					</Link>
				</Box>
				<Box>
					<HStack spacing={4}>
						{showSupportButton && (
						<Box
							as="button"
							type="button"
							onClick={() => navigate(canTicket('ticket-list') ? '/support/tickets' : '/support/create-ticket')}
							color="var(--brand-primary)"
							bg="rgb(var(--brand-primary-rgb) / 0.08)"
							fontSize="13px"
							fontWeight="600"
							px={3}
							py={1.5}
							borderRadius="full"
							transition="background 0.15s"
							_hover={{ bg: 'rgb(var(--brand-primary-rgb) / 0.16)' }}
							aria-label="Contact Support"
						>
							Contact Support
						</Box>
						)}
						<Box>
							<Menu>
								<MenuButton textAlign="left" fontSize="14px">
									<Text mb="0">Welcome</Text>
									<HStack id="Header_User">
										<Box>{email}</Box>
										<Box><ChevronDownIcon boxSize={6} /></Box>
									</HStack>
								</MenuButton>
								<MenuList>
									<MenuItem onClick={Logout}>Logout</MenuItem>
								</MenuList>
							</Menu>
						</Box>
					</HStack>
				</Box>
			</HStack>

			{/* Navigation Menu */}
			<HStack
				h="50px"
				bg="#303030"
				py="30px"
				justifyContent="center"
				alignItems="center"
				color="black"
				fontSize="14px"
				className="header_section"
				borderBottom={"1px"}
				borderColor={"#0000001a"}
				flexWrap="nowrap"
				overflow="visible"
			>
				{navItems.slice(0, NAV_VISIBLE).map(renderMenuItem)}
				{navItems.length > NAV_VISIBLE && (
					<Box flexShrink={0}>
						<NavMegaMenu
							label="More"
							nodes={navItems.slice(NAV_VISIBLE)}
							isActive={navItems.slice(NAV_VISIBLE).some(hasActiveDescendant)}
							isNavActive={isNavActive}
							isHidden={isEmptyParentNode}
						/>
					</Box>
				)}
			</HStack>

			<TwoFactorAuthModal isOpen={isOpen} onClose={onClose} />
		</>
	);
}

export default Header;
